import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { required } from '../../util/validators';

import Modal from '../Modal/Modal';
import Form from '../Form/Form';
import FieldSelect from '../FieldSelect/FieldSelect';
import FieldTextInput from '../FieldTextInput/FieldTextInput';
import { PrimaryButton } from '../Button/Button';

import css from './ReportContentModal.module.css';

const REASON_OPTIONS = [
  'inappropriateContent',
  'harassmentOrAbuse',
  'scamOrFraud',
  'other',
];

const buildMailto = ({ intl, reportType, reportedTitle, reportedUrl, reason, details }) => {
  const reasonLabel = intl.formatMessage({ id: `ReportContentModal.reason.${reason}` });
  const typeLabel = intl.formatMessage({ id: `ReportContentModal.type.${reportType}` });

  const subject = intl.formatMessage(
    { id: 'ReportContentModal.emailSubject' },
    { type: typeLabel, title: reportedTitle }
  );

  const bodyLines = [
    intl.formatMessage({ id: 'ReportContentModal.emailBodyReason' }, { reason: reasonLabel }),
    '',
    details
      ? intl.formatMessage({ id: 'ReportContentModal.emailBodyDetails' }, { details })
      : null,
    details ? '' : null,
    intl.formatMessage(
      { id: 'ReportContentModal.emailBodyReported' },
      { type: typeLabel, title: reportedTitle }
    ),
    reportedUrl ? reportedUrl : null,
  ].filter(line => line !== null);

  const body = bodyLines.join('\n');

  return `mailto:help@nothingtowearclub.ca?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
};

const ReportForm = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const { formId, handleSubmit, intl, invalid, inProgress } = fieldRenderProps;
      const submitDisabled = invalid || inProgress;

      return (
        <Form onSubmit={handleSubmit}>
          <FieldSelect
            id={formId ? `${formId}.reason` : 'reportReason'}
            name="reason"
            className={css.field}
            label={intl.formatMessage({ id: 'ReportContentModal.reasonLabel' })}
            validate={required(intl.formatMessage({ id: 'ReportContentModal.reasonRequired' }))}
          >
            <option disabled value="">
              {intl.formatMessage({ id: 'ReportContentModal.reasonPlaceholder' })}
            </option>
            {REASON_OPTIONS.map(reason => (
              <option key={reason} value={reason}>
                {intl.formatMessage({ id: `ReportContentModal.reason.${reason}` })}
              </option>
            ))}
          </FieldSelect>
          <FieldTextInput
            id={formId ? `${formId}.details` : 'reportDetails'}
            name="details"
            className={css.field}
            type="textarea"
            label={intl.formatMessage({ id: 'ReportContentModal.detailsLabel' })}
            placeholder={intl.formatMessage({ id: 'ReportContentModal.detailsPlaceholder' })}
          />
          <PrimaryButton type="submit" inProgress={inProgress} disabled={submitDisabled}>
            <FormattedMessage id="ReportContentModal.submit" />
          </PrimaryButton>
        </Form>
      );
    }}
  />
);

/**
 * A modal that lets a user report a listing, a user profile, or a conversation.
 * On submit, it opens a pre-filled email to the marketplace operator rather than
 * sending anything automatically — no backend/email service required.
 *
 * @component
 * @param {Object} props
 * @param {string} props.id
 * @param {boolean} props.isOpen
 * @param {Function} props.onCloseModal
 * @param {Function} props.onManageDisableScrolling
 * @param {'listing'|'user'|'conversation'} props.reportType
 * @param {string} props.reportedTitle - Listing title, user's display name, or conversation label
 * @param {string} [props.reportedUrl] - Link to the reported item, included in the email for context
 * @returns {JSX.Element}
 */
const ReportContentModal = props => {
  const intl = useIntl();
  const {
    id,
    isOpen,
    onCloseModal,
    onManageDisableScrolling,
    reportType,
    reportedTitle,
    reportedUrl,
  } = props;
  const [inProgress, setInProgress] = useState(false);

  const handleSubmit = values => {
    setInProgress(true);
    const mailtoUrl = buildMailto({
      intl,
      reportType,
      reportedTitle,
      reportedUrl,
      reason: values.reason,
      details: values.details,
    });
    if (typeof window !== 'undefined') {
      window.location.href = mailtoUrl;
    }
    setInProgress(false);
    onCloseModal();
  };

  return (
    <Modal
      id={id}
      isOpen={isOpen}
      onClose={onCloseModal}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      closeButtonMessage={intl.formatMessage({ id: 'ReportContentModal.close' })}
    >
      <p className={css.modalTitle}>
        <FormattedMessage id="ReportContentModal.title" />
      </p>
      <p className={css.modalMessage}>
        <FormattedMessage id="ReportContentModal.description" />
      </p>
      <ReportForm onSubmit={handleSubmit} intl={intl} inProgress={inProgress} />
    </Modal>
  );
};

export default ReportContentModal;
